/**
 * One shared, gesture-unlocked <audio> element for all spoken output.
 *
 * Mobile Safari (and Chrome's autoplay policy) only lets an audio element play
 * if it was activated during a user gesture. A fresh `new Audio()` per utterance
 * is fine for a click-to-listen button - the click *is* the gesture - but voice
 * mode speaks repeatedly with no further taps, so every utterance after the
 * first would be blocked.
 *
 * The fix is to activate a single element during the tap that enters voice mode
 * and reuse it for the whole session: an element that has been played once stays
 * activated across later `src` changes.
 */

/** Zero-sample WAV. Playing it is a no-op that still marks the element activated. */
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

/**
 * Volume while the user talks over Tally: low enough not to drown them out,
 * and not silent, since the speech may turn out to be a cough.
 */
export const DUCKED_VOLUME = 0.2;

let sharedAudio: HTMLAudioElement | null = null;
let unlocked = false;
let ducked = false;

function createElement(): HTMLAudioElement {
  const audio = new Audio();
  audio.preload = "auto";
  // Keeps iOS from taking over the screen with a native player.
  audio.setAttribute("playsinline", "true");
  return audio;
}

/**
 * Returns the shared element, creating it on first use.
 *
 * Callers must not destroy the element - use `resetAudioElement` to make it
 * ready for the next utterance.
 */
export function acquireAudioElement(): HTMLAudioElement {
  if (!sharedAudio) sharedAudio = createElement();
  return sharedAudio;
}

/**
 * Marks the shared element as user-activated.
 *
 * Must be called *synchronously* from inside a user gesture handler (the tap
 * that starts voice mode). Awaiting anything first loses the gesture.
 */
export function unlockAudioPlayback(): void {
  const audio = acquireAudioElement();
  if (unlocked) return;

  try {
    audio.src = SILENT_WAV;
    audio.muted = true;
    const played = audio.play();
    // Older Safari returns undefined rather than a promise.
    if (played && typeof played.catch === "function") {
      played.catch(() => {
        // Activation can legitimately fail (no gesture, autoplay blocked). The
        // session still works on desktop, so this is not worth surfacing.
      });
    }
    unlocked = true;
  } catch {
    // Same reasoning: never let unlocking break entering voice mode.
  } finally {
    audio.muted = false;
  }
}

/** The volume spoken output should play at right now. */
export function playbackVolume(): number {
  return ducked ? DUCKED_VOLUME : 1;
}

/**
 * Lowers or restores spoken output. Persists across utterances, so a sentence
 * that starts while the user is still talking starts quiet too. iOS ignores
 * element volume, so there this is a no-op.
 */
export function setPlaybackDucked(on: boolean): void {
  ducked = on;
  if (sharedAudio) sharedAudio.volume = playbackVolume();
}

export function isAudioUnlocked(): boolean {
  return unlocked;
}

/**
 * Stops playback and detaches the current source without discarding the
 * element, so its activation survives into the next utterance.
 */
export function resetAudioElement(): void {
  if (!sharedAudio) return;

  sharedAudio.onended = null;
  sharedAudio.onerror = null;
  try {
    sharedAudio.pause();
  } catch {
    // Pausing an element with no source throws in some browsers.
  }

  if (sharedAudio.src.startsWith("blob:")) {
    URL.revokeObjectURL(sharedAudio.src);
  }
  sharedAudio.removeAttribute("src");
  try {
    sharedAudio.load();
  } catch {
    // load() on a source-less element is harmless if it throws.
  }
}

/** Test seam: forget the element and its activation. */
export function __resetAudioPlaybackForTests(): void {
  sharedAudio = null;
  unlocked = false;
  ducked = false;
}
